import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { Loader2, Search, Plus, PencilIcon, Trash2, DollarSign } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { ClientModal } from '../../../components/admin/clients/ClientModal';
import { DeleteConfirmationDialog } from '../../../components/admin/clients/DeleteConfirmationDialog';
import { useToast } from '../../../components/ui/use-toast';
import { Badge } from '../../../components/ui/badge';
import { Pagination } from '../../../components/ui/pagination';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { Link } from 'react-router-dom';
import ClientActiveTable from './ClientActiveTable';

interface Client {
  id: string;
  name: string;
  email: string;
  is_active: boolean;
  permissions: {
    features: string[];
    contests: string[];
  };
}

const getFeatureLabel = (feature: string) => {
  const labels: { [key: string]: string } = {
    'view_stats': 'Voir les statistiques',
    'manage_contestants': 'Gérer les candidats',
    'view_transactions': 'Voir les transactions'
  };
  return labels[feature] || feature;
};

export const ClientList = () => {
  const { user, isLoading: isUserLoading } = useCheckPermissions(['admin']);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [isDeletingClient, setIsDeletingClient] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const ITEMS_PER_PAGE = 20;
  const { toast } = useToast();

  const fetchClients = async () => {
    try {
      setIsLoading(true);

      // Fetch total count first
      const { count } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'client')
        .or(`email.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`);

      setTotalClients(count || 0);

      // Then fetch paginated data
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*')
        .eq('role', 'client')
        .or(`email.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`)
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      setClients(users || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger la liste des clients.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchClients();
    }
  }, [user, currentPage, searchTerm]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search term changes
  }, [searchTerm]);

  const handleCreateClient = () => {
    setSelectedClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleDeleteClient = (client: Client) => {
    setClientToDelete(client);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;

    try {
      setIsDeletingClient(true);

      // Supprimer le client de la table users
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', clientToDelete.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: `Le client ${clientToDelete.name} a été supprimé avec succès`
      });

      // Rafraîchir la liste des clients
      await fetchClients();
      
      // Fermer le modal
      setIsDeleteDialogOpen(false);
      setClientToDelete(null);
    } catch (error: any) {
      console.error('Erreur lors de la suppression du client:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer le client'
      });
    } finally {
      setIsDeletingClient(false);
    }
  };

  const handleModalClose = (refresh?: boolean) => {
    setIsModalOpen(false);
    setSelectedClient(null);
    if (refresh) {
      fetchClients();
    }
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Si le chargement de l'utilisateur est en cours, afficher un loader
  if (isUserLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Si l'utilisateur n'est pas un admin, ne rien afficher
  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <AdminLayout>
      {/* Vue globale des clients actifs */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 flex-wrap">
          <div className="flex-1 w-full md:w-auto min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-yellow-500" />
              <Input
                placeholder="Rechercher un client..."
                className="pl-10 bg-gray-800 border-gray-700 hover:border-yellow-400/30 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/30 text-white placeholder-gray-400 transition-colors w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <Button onClick={handleCreateClient} className="md:ml-4 w-full md:w-auto">
            <Plus className="h-4 w-4 mr-0 md:mr-2" />
            <span className="hidden md:inline">Nouveau client</span>
          </Button>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Nom</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Statut</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Permissions</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-yellow-400 mx-auto" />
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      Aucun client trouvé
                    </td>
                  </tr>
                ) : (
                  clients.map((client, index) => (
                    <tr key={client.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-4 py-3 text-sm text-white">
                        {client.name || 'Sans nom'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {client.email}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant={client.is_active ? 'success' : 'destructive'} className={!client.is_active ? 'bg-gray-700 text-gray-300' : ''}>
                          {client.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-2">
                          {client.permissions?.features?.map((feature) => (
                            <Badge 
                              key={feature} 
                              className={
                                feature === 'view_stats' ? 'bg-blue-400/20 hover:bg-white/10 text-blue-400 border-blue-400/20' :
                                feature === 'manage_contestants' ? 'bg-green-400/20 hover:bg-white/10 text-green-400 border-green-400/20' :
                                feature === 'view_transactions' ? 'bg-purple-400/20 hover:bg-white/10 text-purple-400 border-purple-400/20' :
                                'bg-gray-400/20 hover:bg-white/10 text-gray-400'
                              }
                            >
                              {getFeatureLabel(feature)}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClient(client)}
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-white/5"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClient(client)}
                            className="text-red-500 hover:text-red-400 hover:bg-white/5"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="text-green-500 hover:text-green-400 hover:bg-white/5"
                          >
                            <Link to={`/admin/clients/${client.id}/finances`}>
                              <DollarSign className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && clients.length > 0 && (
            <div className="border-t border-white/10">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalClients / ITEMS_PER_PAGE)}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>

        {/* Tableau détaillé des clients actifs */}
        <ClientActiveTable />

        <ClientModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          client={selectedClient}
        />

        <DeleteConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setClientToDelete(null);
          }}
          onConfirm={confirmDelete}
          clientName={clientToDelete?.name || ''}
          clientEmail={clientToDelete?.email}
          isLoading={isDeletingClient}
        />
      </div>
    </AdminLayout>
  );
};
